import { complianceTargetAtom } from '@/global-store';
import { useFetchAccessibilityData } from '@http/queries';
import { useAtom } from 'jotai';
import { useMemo } from 'react';

const tableLength = 5;

export default function useAccessibilityData() {
    const [complianceTarget] = useAtom(complianceTargetAtom);

    const {
        isLoading: accessibilityDataLoading,
        isError: accessibilityDataError,
        queriesData
    } = useFetchAccessibilityData();

    const { AccessibilityData, isLoading, isError } = useMemo(() => {
        if (accessibilityDataError) {
            console.warn('Error fetching accessibilityData!', accessibilityDataError);
            return { AccessibilityData: null, isLoading: false, isError: true };
        }

        if (!queriesData || !queriesData.report || accessibilityDataLoading) {
            return { AccessibilityData: null, isLoading: true, isError: false };
        }

        const { report, history, wcagRules, eaRules } = queriesData;

        function getTotalWarningsForComplianceTarget(warningsPerConformanceLvl) {
            if (!warningsPerConformanceLvl) return 0;

            let errorCountForComplianceTarget = warningsPerConformanceLvl.A;

            if (complianceTarget.includes('AAA')) {
                errorCountForComplianceTarget +=
                    warningsPerConformanceLvl.AAA + warningsPerConformanceLvl.AA;
            } else if (complianceTarget.includes('AA')) {
                errorCountForComplianceTarget += warningsPerConformanceLvl.AA;
            }

            if (complianceTarget.includes('Best Practice')) {
                errorCountForComplianceTarget += warningsPerConformanceLvl['Best Practice'];
            }

            return errorCountForComplianceTarget;
        }

        function calcTotalWarningsForComplianceTarget(summaryForPageOrDomain) {
            return getTotalWarningsForComplianceTarget(
                summaryForPageOrDomain?.warningsPerConformanceLvl
            );
        }

        function calcAvgErrorCount(indexOffset) {
            const dates = Object.keys(history);
            const date = dates[dates.length - indexOffset];
            const data = history[date];
            if (data !== undefined) {
                const warningsForComplianceTarget = getTotalWarningsForComplianceTarget(
                    data.warningsPerConformanceLvl
                );
                return (
                    warningsForComplianceTarget / Object.keys(data.summaryPerPage).length
                ).toFixed(1);
            } else return '';
        }

        function calcPagesChecked(indexOffset) {
            const dates = Object.keys(history);
            const date = dates[dates.length - indexOffset];
            const data = history[date];
            if (data !== undefined) {
                return Object.keys(data.summaryPerPage).length;
            } else return '';
        }

        /* Function to get OverallScore based on Compliance Target */
        function calcOverallAuditScore(auditScores, isRounded, siteTarget) {
            if (!auditScores) return 0;
            const multi = isRounded ? 1 : 100;
            siteTarget = siteTarget || complianceTarget;
            const complianceTargetParts = siteTarget.split(' + ');

            // return already calculated scores if available
            if (Object.prototype.hasOwnProperty.call(auditScores, 'TargetA')) {
                let complianceTargetScore = `Target${complianceTargetParts[0]}`;
                if (complianceTargetParts[1]) complianceTargetScore += `_BP`;
                return (auditScores[complianceTargetScore] * multi).toFixed(2);
            }

            let overallScore = parseFloat(auditScores.A) * 2;
            let averageCount = 2;

            if (siteTarget.includes('AAA')) {
                overallScore += parseFloat(auditScores.AA) * 2 + parseFloat(auditScores.AAA);
                averageCount += 3;
            } else if (siteTarget.includes('AA')) {
                overallScore += parseFloat(auditScores.AA) * 2;
                averageCount += 2;
            }

            if (siteTarget.includes('Best Practice')) {
                overallScore += parseFloat(auditScores['Best Practice']);
                averageCount += 1;
            }

            return ((overallScore / averageCount) * multi).toFixed(1);
        }

        /* get rules based on selected compliance target */
        function getRulesForSelectedComplianceTarget(rules) {
            const rulesForSelectedComplianceTarget = {};

            for (let [rule, ruleData] of Object.entries(rules)) {
                // map EA-Rule to this rule
                ruleData.eaRule = eaRules[rule];
                const wcagChapter = ruleData.eaRule.categories[0];
                // map WCAG-Rule to this rule
                ruleData.wcagRule = wcagRules[wcagChapter];
                const wcagLvl = ruleData.wcagRule.wcagLvl;

                // set the ruleID as reference
                ruleData.eyeAbleId = rule;

                if (complianceTarget.includes(wcagLvl)) {
                    rulesForSelectedComplianceTarget[rule] = ruleData;
                }
            }
            return rulesForSelectedComplianceTarget;
        }

        const accessibilityDataResult = {};

        const auditScores = report.summary.scores ?? report.summary.auditScores;

        const rules = report.rules;

        accessibilityDataResult.rules = rules;

        accessibilityDataResult.avgErrorCount = [calcAvgErrorCount(1), calcAvgErrorCount(2)];

        accessibilityDataResult.pagesChecked = [calcPagesChecked(1), calcPagesChecked(2)];

        accessibilityDataResult.summaryDetailScore = calcOverallAuditScore(auditScores);

        const rulesForSelectedComplianceTarget = getRulesForSelectedComplianceTarget(rules);

        accessibilityDataResult.rulesForSelectedComplianceTarget = rulesForSelectedComplianceTarget;

        /* Sort the rules by the number of errors */
        const keysSorted = Object.keys(rulesForSelectedComplianceTarget).sort(
            (a, b) =>
                rulesForSelectedComplianceTarget[b].totalErrors -
                rulesForSelectedComplianceTarget[a].totalErrors
        );

        accessibilityDataResult.mostCommonErrors = [];

        for (let i = 0; i < tableLength; i++) {
            const key = keysSorted[i];
            if (rulesForSelectedComplianceTarget[key].totalErrors > 0) {
                accessibilityDataResult.mostCommonErrors.push({
                    key: eaRules[key].content,
                    value: rulesForSelectedComplianceTarget[key]
                });
            }
        }

        accessibilityDataResult.calcOverallAuditScore = calcOverallAuditScore;
        accessibilityDataResult.calcTotalWarningsForComplianceTarget =
            calcTotalWarningsForComplianceTarget;

        accessibilityDataResult.auditScores = auditScores;
        accessibilityDataResult.history = history;
        accessibilityDataResult.report = report;
        accessibilityDataResult.eaRules = eaRules;
        accessibilityDataResult.wcagRules = wcagRules;
        accessibilityDataResult.complianceTarget = complianceTarget;

        return {
            AccessibilityData: accessibilityDataResult,
            isLoading: false,
            isError: false
        };
    }, [accessibilityDataError, accessibilityDataLoading, complianceTarget, queriesData]);

    return {
        AccessibilityData,
        isLoading,
        isError
    };
}
